import React, { useRef,useEffect, useState } from 'react';
import classNames from 'classnames/bind';

import styles from './DatePicker.pcss';

const cx = classNames.bind(styles);

const DatePicker = ({
  onDateSelect,
  setShowCalendar,
  preventDefaultPropagation,
  skipUpdateOncancel = false,
  isEditProfile = false
}) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [showYears, setShowYears] = useState(false);
  const [focusedDate, setFocusedDate] = useState(new Date());

  const firstInteractiveElementRef = useRef(null);
  var openButtonRef = useRef(null)
  const dateRefs = useRef({});
  const hasEnteredGridRef = useRef(false);
  const modalRef = useRef(null);


  useEffect(() => {
    openButtonRef.current = document.activeElement;
    firstInteractiveElementRef.current?.focus();

    const handleKeyDown = (e) => {
      if (!modalRef.current) return;

      // ESC closes modal
      if (e.key === "Escape") {
        e.preventDefault();
        setShowCalendar(false);
        openButtonRef?.current?.focus();
        return;
      }

      if (e.key !== "Tab") return;

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      const focusable = Array.from(focusableElements).filter(
        el => !el.hasAttribute("disabled") && !el.getAttribute("aria-disabled")
      );

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        // SHIFT + TAB
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // TAB
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    setFocusedDate(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    if (!hasEnteredGridRef.current) return;

    const key = `${focusedDate.getFullYear()}-${focusedDate.getMonth()}-${focusedDate.getDate()}`;
    const el = dateRefs.current[key];
    if (el) {
      el.focus();
    }
  }, [focusedDate]);

  const toggleShowYears = () => {
    setShowYears(!showYears);
  };

  const handleGridKeyDown = (e) => {
    const today = new Date();
    const newDate = new Date(focusedDate);

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        newDate.setDate(newDate.getDate() + 1);
        break;

      case "ArrowLeft":
        e.preventDefault();
        newDate.setDate(newDate.getDate() - 1);
        break;

      case "ArrowDown":
        e.preventDefault();
        newDate.setDate(newDate.getDate() + 7);
        break;

      case "ArrowUp":
        e.preventDefault();
        newDate.setDate(newDate.getDate() - 7);
        break;

      case "Home":
        e.preventDefault();
        newDate.setDate(1);
        break;

      case "End":
        e.preventDefault();
        newDate.setDate(
          new Date(
            selectedDate.getFullYear(),
            selectedDate.getMonth() + 1,
            0
          ).getDate()
        );
        break;

      case "Enter":
      case " ":
        e.preventDefault();
        handleDateSelect(focusedDate.getDate());
        return;

      default:
        return;
    }

    // STOP if outside current month
    if (
      newDate.getMonth() !== selectedDate.getMonth() ||
      newDate.getFullYear() !== selectedDate.getFullYear()
    ) {
      return;
    }

    // STOP if future date
    if (newDate > today) {
      return;
    }

    setFocusedDate(newDate);
  };

  const onCancelClick = () => {
    !skipUpdateOncancel && onDateSelect(new Date());
    setShowCalendar(false);
    openButtonRef?.current?.focus()
  };

  const okClick = () => {
    onDateSelect(selectedDate);
    setShowCalendar(false);
    openButtonRef?.current?.focus()
  };

  // Function to handle year change
  const handleYearChange = event => {
    const year = parseInt(event.target.value);
    const newDate = new Date(selectedDate);
    newDate.setFullYear(year);
    setSelectedDate(newDate);
  };

  // Function to handle month change
  const changeMonth = (event, increment) => {
    event.stopPropagation();

    if (isEditProfile) {
      event.preventDefault();
    }

    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + increment);

    // Get current year and month
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();

    // Check if the new month is in the future
    if (
      newDate.getFullYear() > currentYear ||
      (newDate.getFullYear() === currentYear && newDate.getMonth() > currentMonth)
    ) {
      return;
    }
    setSelectedDate(newDate);
  };

  // Function to generate years from current year minus 100
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 100; year--) {
      years.push(year);
    }
    return years;
  };

  const getCalendarDays = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();
    const daysInMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth() + 1,
      0
    ).getDate();
    const firstDayIndex = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      1
    ).getDay();

    const cells = [];

    // Previous month empty cells
    for (let i = 0; i < firstDayIndex; i++) {
      cells.push(
        <div
          role="gridcell"
          className={cx('day', 'disabled')}
          key={`prev-${i}`}
          aria-disabled="true"
        />
      );
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        i
      );

      const isSelected =
        selectedDate && selectedDate.getDate() === i;

      const isDisabled =
        currentDate > new Date(currentYear, currentMonth, currentDay);

      const isFocused =
        focusedDate &&
        focusedDate.getDate() === i &&
        focusedDate.getMonth() === selectedDate.getMonth() &&
        focusedDate.getFullYear() === selectedDate.getFullYear();

      const key = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${i}`;

      cells.push(
        <div
          role="gridcell"
          key={key}
          ref={(el) => (dateRefs.current[key] = el)}
          className={cx(
            'day',
            isSelected && 'selected',
            isDisabled && 'disabled'
          )}
          aria-selected={isSelected}
          aria-disabled={isDisabled}
          tabIndex={isFocused ? 0 : -1}
          onFocus={() => {
            hasEnteredGridRef.current = true;
          }}
          onKeyDown={(e) => handleGridKeyDown(e, isDisabled)}
          onClick={() => !isDisabled && handleDateSelect(i)}
        >
          {i}
        </div>
      );
    }

    // Fill remaining cells to complete last row
    while (cells.length % 7 !== 0) {
      const index = cells.length;
      cells.push(
        <div
          role="gridcell"
          className={cx('day', 'disabled')}
          key={`next-${index}`}
          aria-disabled="true"
        />
      );
    }

    // Chunk into rows of 7
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) {
      rows.push(
        <div role="row" key={`row-${i}`} className={cx('week-row')}>
          {cells.slice(i, i + 7)}
        </div>
      );
    }

    return rows;
  };

  const handleDateSelect = day => {
    const currentDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
    const today = new Date();
    if (currentDate <= today) {
      const selectedDateCopy = new Date(selectedDate);
      selectedDateCopy.setDate(day);
      setSelectedDate(selectedDateCopy);
    }
  };

  const isNextArrowDisabled = () => {
    const today = new Date();
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    const nextMonthStart = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth() + 1,
      1
    );

    return nextMonthStart > currentMonthStart;
  };

  return (
    <div ref={modalRef} className={cx('date-picker')} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="datepicker-heading" >
      <div className={cx('first-row')}>
        <div id="datepicker-heading" className='sr-only'>Date Picker</div>
      
        <div className={cx('year-dropdown')}>
          <label className='sr-only' htmlFor='year-combo' >Year</label>
          <select id="year-combo" onChange={handleYearChange} ref={firstInteractiveElementRef}>
            {generateYears().map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div 
          className={cx('selected-date')}
          aria-label={selectedDate.toLocaleDateString('en-US', {
            weekday: 'long', 
            day: 'numeric', 
            month: 'long',  
          })}
        >
          <span>
            {selectedDate.toLocaleDateString('en-US', { weekday: 'short' })}{' '}
            {selectedDate.getDate()}, {selectedDate.toLocaleDateString('en-US', { month: 'short' })}
          </span>
        </div>
      </div>
      <div className={cx('month-navigation')}>
        <button onClick={event => changeMonth(event, -1)} aria-label="Previous month">&lt;</button>
        <h2
          id="month-name"
          aria-label={selectedDate.toLocaleDateString('default', {
            month: 'long',   
            year: 'numeric', 
          })}  
        >{selectedDate.toLocaleDateString('default', { month: 'long', year: 'numeric' })}</h2>
        <button
          className={cx(isNextArrowDisabled() ? 'disabled' : '')}
          onClick={e => changeMonth(e, 1)}
          aria-disabled={isNextArrowDisabled()}
          tabIndex={isNextArrowDisabled() ? -1 : 0}
          aria-label="Next month"
        >
          &gt;
        </button>
      </div>
      <div className={cx('calendar')}>
        <div className={cx('days')} role='grid' aria-labelledby='month-name' onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) {
            setFocusedDate(selectedDate);
          }
        }}>
          <div role="row" className={cx('week-row')}>
            <div className={cx('day-name')} role="columnheader">
              <span aria-label="Sunday"><span aria-hidden="true">S</span></span>
            </div>
            <div className={cx('day-name')} role="columnheader" >
              <span aria-label="Monday"><span aria-hidden="true">M</span></span>
            </div>
            <div className={cx('day-name')} role="columnheader" >
              <span aria-label="Tuesday"><span aria-hidden="true">T</span></span>
            </div>
            <div className={cx('day-name')} role="columnheader" >
              <span aria-label="Wednesday"><span aria-hidden="true">W</span></span>
            </div>
            <div className={cx('day-name')} role="columnheader" >
              <span aria-label="Thursday"><span aria-hidden="true">T</span></span>
            </div>
            <div className={cx('day-name')} role="columnheader" >
              <span aria-label="Friday"><span aria-hidden="true">F</span></span>
            </div>
            <div className={cx('day-name')} role="columnheader" >
              <span aria-label="Saturday"><span aria-hidden="true">S</span></span>
            </div>
          </div>
          {getCalendarDays()}
        </div>
      </div>
      <div className={cx('ok-cancel')}>
        <div role="button" tabIndex={0} className={cx('ok')} onClick={() => okClick()}>
          OK
        </div>
        <div role="button" tabIndex={0} className={cx('cancel')} onClick={() => onCancelClick()}>
          Cancel
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
